import { useRef, useCallback, useState } from 'react';
import { useLanguage } from '../../i18n';
import type { InputMode, ScreenshotAnalysis } from './types';
import { isPlanningFileValid, generateDisplayFileName } from './types';

// 파일 미리보기 모달 타입
interface FilePreviewState {
  isOpen: boolean;
  fileName: string;
  fileType: 'csv' | 'text' | 'pdf' | 'image' | 'unknown';
  content: string | null;
  blobUrl: string | null;
}

interface PlanningSectionProps {
  planningIntent: string;
  setPlanningIntent: (value: string) => void;
  planningInputMode: InputMode;
  setPlanningInputMode: (mode: InputMode) => void;
  planningFiles: File[];
  setPlanningFiles: React.Dispatch<React.SetStateAction<File[]>>;
  screenshotAnalyses: ScreenshotAnalysis[];
  extractedPlanningItems: string[];
  isAnalyzing: boolean;
  analyzePlanningFiles: (files: File[], featureName?: string) => Promise<void>;
  clearPlanningAnalysis: () => void;
  showFileError: (message: string) => void;
  charCount?: number;
  charLimit?: number;
  isDemo?: boolean;
  featureName?: string;
  isFreePlan?: boolean;
}

// CSV 파싱 함수
const parseCsv = (csvText: string): string[][] => {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentCell = '';
  let inQuotes = false;
  
  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];
    
    if (inQuotes) {
      if (char === '"' && nextChar === '"') {
        currentCell += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        currentCell += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        currentRow.push(currentCell);
        currentCell = '';
      } else if (char === '\n' || (char === '\r' && nextChar === '\n')) {
        currentRow.push(currentCell);
        rows.push(currentRow);
        currentRow = [];
        currentCell = '';
        if (char === '\r') i++;
      } else if (char !== '\r') {
        currentCell += char;
      }
    }
  }
  
  if (currentCell || currentRow.length > 0) {
    currentRow.push(currentCell);
    rows.push(currentRow);
  }
  
  return rows;
};

// 파일 확장자에 따른 미리보기 타입 결정
const getFilePreviewType = (fileName: string): 'csv' | 'text' | 'pdf' | 'image' | 'unknown' => {
  const ext = fileName.toLowerCase().split('.').pop() || '';
  if (ext === 'csv') return 'csv';
  if (['txt', 'md', 'markdown', 'json', 'xml', 'html', 'log'].includes(ext)) return 'text';
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
  return 'unknown';
};

function PlanningSection({
  planningIntent,
  setPlanningIntent,
  planningInputMode,
  setPlanningInputMode,
  planningFiles,
  setPlanningFiles,
  screenshotAnalyses,
  extractedPlanningItems,
  isAnalyzing,
  analyzePlanningFiles,
  clearPlanningAnalysis,
  showFileError,
  charCount = 0,
  charLimit = 3000,
  isDemo = false,
  featureName = '',
  isFreePlan = false,
}: PlanningSectionProps) {
  const { lang, t } = useLanguage();
  const planningFileRef = useRef<HTMLInputElement>(null);

  // 파일 미리보기 상태
  const [previewState, setPreviewState] = useState<FilePreviewState>({
    isOpen: false,
    fileName: '',
    fileType: 'unknown',
    content: null,
    blobUrl: null,
  });

  // 파일 미리보기 핸들러
  const handlePreviewFile = useCallback(async (file: File) => {
    const fileType = getFilePreviewType(file.name);
    
    if (fileType === 'unknown') {
      showFileError(lang === 'ko' ? '이 파일 형식은 미리보기를 지원하지 않습니다.' : 'Preview is not supported for this file type.');
      return;
    }

    if (fileType === 'csv' || fileType === 'text') {
      const text = await file.text();
      setPreviewState({
        isOpen: true,
        fileName: file.name,
        fileType,
        content: text,
        blobUrl: null,
      });
    } else {
      const blobUrl = URL.createObjectURL(file);
      setPreviewState({
        isOpen: true,
        fileName: file.name,
        fileType,
        content: null,
        blobUrl,
      });
    }
  }, [lang, showFileError]);

  // 미리보기 닫기
  const closePreview = useCallback(() => {
    if (previewState.blobUrl) {
      URL.revokeObjectURL(previewState.blobUrl);
    }
    setPreviewState({
      isOpen: false,
      fileName: '',
      fileType: 'unknown',
      content: null,
      blobUrl: null,
    });
  }, [previewState.blobUrl]);

  const handlePlanningFileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files);
      const validFiles = files.filter(isPlanningFileValid);
      const invalidFiles = files.filter((f) => !isPlanningFileValid(f));

      if (invalidFiles.length > 0) {
        const names = invalidFiles.map((f) => f.name).join(', ');
        showFileError(
          lang === 'ko'
            ? `⚠️ 지원하지 않는 파일 형식: ${names}\n(이미지, PDF, 텍스트만 지원)`
            : `⚠️ Unsupported file format: ${names}\n(Only images, PDF, text supported)`
        );
      }

      if (validFiles.length > 0) {
        setPlanningFiles((prev) => [...prev, ...validFiles]);
        analyzePlanningFiles([...planningFiles, ...validFiles], featureName);
      }
    },
    [analyzePlanningFiles, planningFiles, showFileError, lang, setPlanningFiles, featureName]
  );

  const handlePlanningFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      const validFiles = files.filter(isPlanningFileValid);
      const invalidFiles = files.filter((f) => !isPlanningFileValid(f));

      if (invalidFiles.length > 0) {
        const names = invalidFiles.map((f) => f.name).join(', ');
        showFileError(
          lang === 'ko'
            ? `⚠️ 지원하지 않는 파일 형식: ${names}\n(이미지, PDF, 텍스트만 지원)`
            : `⚠️ Unsupported file format: ${names}\n(Only images, PDF, text supported)`
        );
      }

      if (validFiles.length > 0) {
        setPlanningFiles((prev) => [...prev, ...validFiles]);
        analyzePlanningFiles([...planningFiles, ...validFiles], featureName);
      }
    },
    [analyzePlanningFiles, planningFiles, showFileError, lang, setPlanningFiles, featureName]
  );

  const removePlanningFile = useCallback(
    (index: number) => {
      const newFiles = planningFiles.filter((_, i) => i !== index);
      setPlanningFiles(newFiles);
      if (newFiles.length > 0) {
        analyzePlanningFiles(newFiles, featureName);
      } else {
        clearPlanningAnalysis();
        setPlanningIntent('');
      }
    },
    [planningFiles, analyzePlanningFiles, clearPlanningAnalysis, setPlanningIntent, setPlanningFiles, featureName]
  );

  const clearPlanningFiles = useCallback(() => {
    setPlanningFiles([]);
    clearPlanningAnalysis();
    setPlanningIntent('');
    if (planningFileRef.current) planningFileRef.current.value = '';
  }, [clearPlanningAnalysis, setPlanningIntent, setPlanningFiles]);

  return (
    <div className="card p-4" data-testid="planning-intent-section">
      <div className="flex items-center justify-between mb-3">
        <div className="text-terminal-muted text-xs uppercase tracking-wide">
          # {t.form.planningIntent}
        </div>
        {/* 입력 모드 토글 - 데모 모드에서는 숨김 */}
        {!isDemo && (
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => {
                setPlanningInputMode('manual');
                clearPlanningFiles();
              }}
              className={`px-2 py-1 rounded transition-colors ${
                planningInputMode === 'manual'
                  ? 'bg-status-go/20 text-status-go border border-status-go/30'
                  : 'text-terminal-muted hover:text-terminal-text'
              }`}
            >
              {t.form.manualInput}
            </button>
            {isFreePlan ? (
              <div 
                className="px-2 py-1 rounded text-terminal-dim cursor-not-allowed flex items-center gap-1 opacity-60"
                title={lang === 'ko' ? 'Pro 이상 플랜에서 사용 가능' : 'Available for Pro plan and above'}
              >
                <span>🔒</span>
                <span>{t.form.fileUpload}</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setPlanningInputMode('file')}
                className={`px-2 py-1 rounded transition-colors ${
                  planningInputMode === 'file'
                    ? 'bg-status-go/20 text-status-go border border-status-go/30'
                    : 'text-terminal-muted hover:text-terminal-text'
                }`}
              >
                {t.form.fileUpload}
              </button>
            )}
          </div>
        )}
      </div>

      {planningInputMode === 'manual' ? (
        <>
          <textarea
            value={planningIntent}
            onChange={(e) => setPlanningIntent(e.target.value)}
            placeholder={t.form.planningPlaceholder}
            className={`terminal-input min-h-[80px] resize-none ${charCount > charLimit ? 'border-status-danger' : ''} ${isDemo ? 'bg-terminal-input/60 cursor-not-allowed' : ''}`}
            rows={3}
            readOnly={isDemo}
            data-testid="input-planning-intent"
          />
          <div className="mt-1 flex items-center justify-between">
            <p className="text-terminal-dim text-xs">{t.form.onePerLine}</p>
            <p className={`text-xs font-medium ${
              charCount > charLimit 
                ? 'text-status-danger' 
                : charCount > charLimit * 0.8 
                  ? 'text-[var(--neo-orange)]' 
                  : 'text-terminal-muted'
            }`}>
              {charCount.toLocaleString()} / {charLimit.toLocaleString()}
              {lang === 'ko' ? '자' : ' chars'}
            </p>
          </div>
        </>
      ) : (
        <div className="space-y-3">
          {/* 파일 드롭 영역 (항상 표시) */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handlePlanningFileDrop}
            onClick={() => planningFileRef.current?.click()}
            className="border-2 border-dashed border-terminal-border rounded-lg p-4 text-center cursor-pointer hover:border-status-go/50 transition-colors"
          >
            <input
              ref={planningFileRef}
              type="file"
              accept="image/*,.pdf,.txt,.md"
              onChange={handlePlanningFileChange}
              multiple
              className="hidden"
            />
            <div className="text-terminal-muted text-sm mb-1">
              {planningFiles.length > 0
                ? lang === 'ko'
                  ? '📎 파일 추가하기'
                  : '📎 Add more files'
                : t.form.dragDropFile}
            </div>
            <div className="text-terminal-dim text-xs">{t.form.supportedFormats}</div>
          </div>

          {/* 업로드된 파일 목록 */}
          {planningFiles.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-terminal-dim text-xs">
                  {lang === 'ko'
                    ? `${planningFiles.length}개 파일`
                    : `${planningFiles.length} file(s)`}
                </span>
                <button
                  type="button"
                  onClick={clearPlanningFiles}
                  className="text-xs text-status-danger hover:underline"
                >
                  {lang === 'ko' ? '전체 삭제' : 'Clear all'}
                </button>
              </div>
              {planningFiles.map((file, index) => {
                const displayName = generateDisplayFileName(featureName, 'planning', index, file.name);
                return (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-terminal-input rounded p-2 text-sm"
                  >
                    <div 
                      className="flex items-center gap-2 truncate cursor-pointer hover:opacity-80 flex-1 min-w-0"
                      onClick={() => handlePreviewFile(file)}
                      title={lang === 'ko' ? '클릭하여 미리보기' : 'Click to preview'}
                    >
                      <span>{file.type.startsWith('image/') ? '🖼️' : '📄'}</span>
                      <span className="text-terminal-text truncate hover:text-status-go transition-colors">
                        {displayName}
                        <span className="ml-1 text-xs text-terminal-muted opacity-60">👁️</span>
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removePlanningFile(index)}
                      className="text-terminal-dim hover:text-status-danger ml-2 flex-shrink-0"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* 분석 중 표시 */}
          {isAnalyzing && (
            <div className="flex items-center gap-2 text-terminal-muted text-sm">
              <div className="w-4 h-4 border-2 border-status-go/30 border-t-status-go rounded-full animate-spin"></div>
              <span>{t.form.analyzing}</span>
            </div>
          )}

          {/* 스크린샷 분석 결과 (다중 파일) */}
          {screenshotAnalyses.length > 0 && !isAnalyzing && (
            <div className="bg-terminal-bg rounded p-3 border border-terminal-border space-y-4">
              <div className="text-terminal-dim text-xs uppercase">
                🔍 {t.form.screenshotAnalysisItems} ({screenshotAnalyses.length})
              </div>

              {/* UI 구성요소 (모든 파일 통합) */}
              <div>
                <div className="text-xs text-status-go mb-1 font-medium">
                  📱 {t.form.uiComponents}
                </div>
                <ul className="space-y-0.5 text-sm text-terminal-muted max-h-32 overflow-y-auto">
                  {screenshotAnalyses.flatMap((a) => a.uiComponents).map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-terminal-dim">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* 사용자 플로우 */}
              <div>
                <div className="text-xs text-status-warning mb-1 font-medium">
                  🔄 {t.form.userFlow}
                </div>
                <ul className="space-y-0.5 text-sm text-terminal-muted">
                  {screenshotAnalyses.flatMap((a) => a.userFlow).map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-terminal-dim">→</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* KPI 제안 (기획 의도로 사용) */}
              <div className="pt-2 border-t border-terminal-border">
                <div className="text-xs text-terminal-text mb-1 font-medium">
                  🎯 {t.form.kpiSuggestions} → {t.form.planningIntent}
                </div>
                <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                  {extractedPlanningItems.map((item, i) => (
                    <li key={i} className="text-sm text-terminal-text flex items-start gap-2">
                      <span className="text-status-go">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* 일반 텍스트/PDF 파일 추출 결과 (스크린샷 분석이 없을 때만) */}
          {screenshotAnalyses.length === 0 &&
            extractedPlanningItems.length > 0 &&
            !isAnalyzing && (
              <div className="bg-terminal-bg rounded p-3 border border-terminal-border">
                <div className="text-terminal-dim text-xs mb-2 uppercase">
                  ✨ {t.form.extractedItems}
                </div>
                <ul className="space-y-1">
                  {extractedPlanningItems.map((item, i) => (
                    <li key={i} className="text-sm text-terminal-text flex items-start gap-2">
                      <span className="text-status-go">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
        </div>
      )}

      {/* 파일 미리보기 모달 */}
      {previewState.isOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={closePreview}
        >
          <div
            className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl flex flex-col overflow-hidden w-[90vw] h-[85vh] max-w-[1200px]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 미리보기 헤더 */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-terminal-border bg-terminal-dim/20">
              <div className="flex items-center gap-2">
                <span className="text-lg">
                  {previewState.fileType === 'csv' ? '📊' :
                   previewState.fileType === 'text' ? '📄' :
                   previewState.fileType === 'pdf' ? '📑' :
                   previewState.fileType === 'image' ? '🖼️' : '📁'}
                </span>
                <div>
                  <h3 className="text-sm font-bold text-terminal-text truncate max-w-[50vw]">
                    {previewState.fileName}
                  </h3>
                  <p className="text-xs text-terminal-muted">
                    {lang === 'ko' ? '미리보기' : 'Preview'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closePreview}
                className="p-1.5 hover:bg-terminal-dim/50 rounded-lg transition-colors text-terminal-muted hover:text-terminal-text"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* 미리보기 콘텐츠 */}
            <div className="flex-1 overflow-auto p-4">
              {previewState.fileType === 'csv' && previewState.content ? (
                <div className="overflow-auto h-full">
                  <table className="min-w-full border-collapse text-sm">
                    <thead className="sticky top-0 bg-terminal-bg z-10">
                      {parseCsv(previewState.content).slice(0, 1).map((row, rowIdx) => (
                        <tr key={rowIdx}>
                          {row.map((cell, cellIdx) => (
                            <th 
                              key={cellIdx}
                              className="px-2 py-1.5 text-left font-semibold text-terminal-text bg-terminal-dim/40 border border-terminal-border whitespace-nowrap text-xs"
                            >
                              {cell}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {parseCsv(previewState.content).slice(1).map((row, rowIdx) => (
                        <tr key={rowIdx} className="hover:bg-terminal-dim/20">
                          {row.map((cell, cellIdx) => (
                            <td 
                              key={cellIdx}
                              className="px-2 py-1.5 text-terminal-text border border-terminal-border max-w-[300px] truncate text-xs"
                              title={cell}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : previewState.fileType === 'text' && previewState.content ? (
                <pre className="p-3 bg-terminal-dim/20 rounded-lg border border-terminal-border text-terminal-text text-xs font-mono whitespace-pre-wrap overflow-auto h-full">
                  {previewState.content}
                </pre>
              ) : previewState.fileType === 'pdf' && previewState.blobUrl ? (
                <object
                  data={previewState.blobUrl}
                  type="application/pdf"
                  className="w-full h-full rounded-lg border border-terminal-border"
                >
                  <div className="h-full flex items-center justify-center text-terminal-muted">
                    <a
                      href={previewState.blobUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-status-go text-white rounded-lg hover:bg-status-go/90"
                    >
                      {lang === 'ko' ? '새 탭에서 열기' : 'Open in new tab'}
                    </a>
                  </div>
                </object>
              ) : previewState.fileType === 'image' && previewState.blobUrl ? (
                <div className="h-full flex items-center justify-center">
                  <img
                    src={previewState.blobUrl}
                    alt={previewState.fileName}
                    className="max-w-full max-h-full object-contain rounded-lg border border-terminal-border"
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlanningSection;

